import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Briefcase, ChevronRight, CheckCircle2, UserCheck, Star, Zap } from 'lucide-react';
import JobPortalHeader from '../components/JobPortal/JobPortalHeader';
import Footer from '../components/Footer';

const JobPortalLanding: React.FC = () => {
    const [recentJobs, setRecentJobs] = useState<any[]>([]);
    const [content, setContent] = useState<any>({});
    const { t, t_api } = useLanguage();

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const res = await fetch('/api/content/Careers_Portal');
                if (res.ok) {
                    const data = await res.json();
                    setContent(data);
                }
            } catch (err) {
                console.error("Content fetch failed", err);
            }
        };

        const fetchJobs = async () => {
            try {
                const res = await fetch('/api/jobs');
                if (res.ok) {
                    const data = await res.json();
                    setRecentJobs(data.filter((j: any) => j.status === 'Active').slice(0, 3));
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchContent();
        fetchJobs();
    }, []);

    const getImg = (key: string, fallback: string) => {
        const val = content[key];
        if (!val) return fallback;
        if (val.startsWith('http')) return val;
        return `https://hamdanoman.com${val}`;
    };

    return (
        <div className="min-h-screen bg-white font-sans pt-24">
            <JobPortalHeader />

            {/* Hero Section with Full Background */}
            <section className="relative h-[600px] flex items-center justify-center overflow-hidden border-b-8 border-red-600">
                <div className="absolute inset-0 z-0">
                    <img 
                        src={getImg('hero_bg', '/Careers.jpg')} 
                        alt="Background" 
                        className="w-full h-full object-cover brightness-[0.4]"
                    />
                </div>
                <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-3xl md:text-5xl font-black mb-6 text-white tracking-wide"
                    >
                        {t_api(content.hero_title || 'HTG Careers', content.hero_title_ar)}
                    </motion.h2>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-xl md:text-2xl font-bold mb-10 text-yellow-400  tracking-widest"
                    >
                        {t_api(content.hero_subtitle, content.hero_subtitle_ar) || t('careers.landing.hero_subtitle')}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 }}
                    >
                        <a href="/job-portal/jobs" className="inline-block bg-red-600 hover:bg-black text-white font-black py-5 px-12 rounded-none shadow-2xl transition-all text-xl  tracking-widest transform hover:scale-105 border-2 border-transparent hover:border-yellow-400">
                            {t_api(content.hero_button_text, content.hero_button_text_ar) || t('careers.landing.apply_now')}
                        </a>
                    </motion.div>
                </div>
                
                {/* Scroll Indicator */}
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
                    <div className="w-1 h-12 bg-gradient-to-b from-yellow-400 to-transparent rounded-full" />
                </div>
            </section>

            {/* About Section - Spirit of HTG Style */}
            <section className="py-10 px-6 md:px-20 max-w-7xl mx-auto flex flex-col lg:flex-row gap-20 items-center overflow-hidden">
                <div className="flex-1 relative">
                    <motion.div 
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-sm font-black text-red-600  tracking-[0.3em] mb-4 uppercase">{t_api(content.about_tag, content.about_tag_ar) || t('careers.landing.culture')}</h2>
                        <h3 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-8 tracking-wide uppercase">
                            {t_api(content.about_title, content.about_title_ar) || t('careers.landing.join_us')}
                        </h3>
                        <p className="text-lg text-slate-600 leading-relaxed font-medium whitespace-pre-line mb-10">
                            {t_api(content.about_text, content.about_text_ar) || `At Hamdan Trading Group we are restless and passionate about making the world more connected, inclusive, and sustainable. We believe in the power of technology and trading harnessed by the human spirit to create a better tomorrow.`}
                        </p>
                        <div className="flex gap-8">
                            <div className="flex flex-col">
                                <span className="text-3xl font-black text-slate-900">{content.stats_nationalities || '30+'}</span>
                                <span className="text-xs font-bold text-slate-400  tracking-widest">{t('careers.landing.nationalities')}</span>
                            </div>
                            <div className="h-12 w-px bg-slate-200" />
                            <div className="flex flex-col">
                                <span className="text-3xl font-black text-slate-900">{content.stats_years || '45+'}</span>
                                <span className="text-xs font-bold text-slate-400  tracking-widest">{t('careers.landing.years')}</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
                <div className="flex-1 relative">
                    <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                        <img src={getImg('about_image', '/about.jpeg')} alt="Spirit" className="w-full h-[500px] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-tr from-red-600/40 to-transparent flex items-end p-8">
                            <h3 className="text-2xl md:text-3xl font-black text-white leading-tight" dangerouslySetInnerHTML={{ __html: t_api(content.spirit_text, content.spirit_text_ar) || `THE SPIRIT<br/><span class="text-yellow-400">${t('nav.group')}</span>` }} />
                        </div>
                    </div>
                    <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
                </div>
            </section>

            {/* Why Join HTG Section */}
            <section id="why-join" className="py-10 bg-slate-50 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="text-center mb-20">
                        <h2 className="text-sm font-black text-red-600  tracking-[0.3em] mb-4 uppercase">{t_api(content.benefits_tag, content.benefits_tag_ar) || t('careers.landing.benefits')}</h2>
                        <h3 className="text-3xl md:text-4xl font-black text-slate-900 tracking-wide">{t_api(content.benefits_title, content.benefits_title_ar) || t('careers.landing.why_join')}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all">
                            <div className="w-14 h-14 bg-yellow-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-yellow-400/20">
                                <Zap className="w-7 h-7 text-black" />
                            </div>
                            <h4 className="text-lg font-black text-slate-900 mb-3  tracking-tight">{t_api(content.benefit1_title, content.benefit1_title_ar) || t('careers.landing.shape_future')}</h4>
                            <p className="text-slate-500 font-bold text-sm leading-relaxed">{t_api(content.benefit1_desc, content.benefit1_desc_ar) || 'Embrace a world of experiences that will challenge you and help you grow.'}</p>
                        </div>
                        <div className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all">
                            <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-red-600/20">
                                <Star className="w-7 h-7 text-white" />
                            </div>
                            <h4 className="text-xl font-black text-slate-900 mb-3  tracking-tight">{t_api(content.benefit2_title, content.benefit2_title_ar) || t('careers.landing.take_lead')}</h4>
                            <p className="text-slate-500 font-bold text-sm leading-relaxed">{t_api(content.benefit2_desc, content.benefit2_desc_ar) || 'Innovation is at our heart, giving you the freedom, space, and tools to create the future.'}</p>
                        </div>
                        <div className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all">
                            <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-black/20">
                                <UserCheck className="w-7 h-7 text-white" />
                            </div>
                            <h4 className="text-xl font-black text-slate-900 mb-3  tracking-tight">{t_api(content.benefit3_title, content.benefit3_title_ar) || t('careers.landing.be_yourself')}</h4>
                            <p className="text-slate-500 font-bold text-sm leading-relaxed">{t_api(content.benefit3_desc, content.benefit3_desc_ar) || "Join a dynamic global community where everyone is welcome. We're positive, inclusive and open."}</p>
                        </div>
                        <div className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all">
                            <div className="w-14 h-14 bg-yellow-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-yellow-400/20">
                                <CheckCircle2 className="w-7 h-7 text-black" />
                            </div>
                            <h4 className="text-xl font-black text-slate-900 mb-3  tracking-tight">{t_api(content.benefit4_title, content.benefit4_title_ar) || t('careers.landing.make_mark')}</h4>
                            <p className="text-slate-500 font-bold text-sm leading-relaxed">{t_api(content.benefit4_desc, content.benefit4_desc_ar) || 'Make an impact on people, businesses, customers, communities and our planet.'}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Vacancies Section - Premium White/Black Contrast */}
            <section className="py-20 bg-white text-slate-900 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 via-yellow-400 to-red-600" />
                <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-red-600 font-black tracking-[0.2em] mb-4 uppercase text-xs">{t_api(content.vacancies_tag, content.vacancies_tag_ar) || t('careers.vacancies_tag')}</p>
                        <h2 className="text-2xl font-black mb-16 tracking-tight text-black uppercase flex items-center justify-center gap-4">
                            <span className="w-1.5 h-8 bg-red-600 rounded-full"></span>
                            {t_api(content.vacancies_title, content.vacancies_title_ar) || t('careers.vacancies')}
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                            {recentJobs.length > 0 ? (
                                recentJobs.map((job) => (
                                    <div key={job.id} className="bg-white p-10 rounded-3xl shadow-2xl text-slate-900 text-left border-b-8 border-yellow-400 group hover:-translate-y-2 transition-all duration-500">
                                        <div className="flex items-center gap-2 text-[10px] font-black text-red-600  tracking-widest mb-4">
                                            <Briefcase className="w-3.5 h-3.5" /> {t_api(job.careerLevel, job.careerLevel_ar)}
                                        </div>
                                        <h3 className="text-2xl font-black mb-6 group-hover:text-red-600 transition-colors  tracking-tight leading-tight">{t_api(job.title, job.title_ar)}</h3>
                                        <div className="space-y-4 text-slate-500 font-bold text-sm mb-10">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-red-50 transition-colors">
                                                    <Briefcase className="w-4 h-4 text-red-600" />
                                                </div>
                                                {t_api(job.department, job.department_ar)}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-red-50 transition-colors">
                                                    <MapPin className="w-4 h-4 text-red-600" />
                                                </div>
                                                {t_api(job.location, job.location_ar)}
                                            </div>
                                        </div>
                                        <a 
                                            href="/job-portal/jobs" 
                                            className="inline-block bg-red-600 hover:bg-black text-white font-black py-4 px-10 rounded-xl transition-all shadow-lg active:scale-95  tracking-widest text-xs"
                                        >
                                            {t('careers.landing.view_apply')}
                                        </a>
                                    </div>
                                ))
                            ) : (
                                <p className="col-span-full text-xl opacity-70 italic font-medium">No current vacancies. Check back soon!</p>
                            )}
                        </div>

                        <div className="mt-20">
                            <a href="/job-portal/jobs" className="bg-yellow-400 hover:bg-black hover:text-white text-black font-black py-5 px-16 rounded-xl transition-all shadow-2xl  tracking-widest text-lg inline-flex items-center gap-4 group">
                                {t('careers.landing.view_all')} <ChevronRight className="group-hover:translate-x-2 transition-transform" />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Getting Started Section - Premium HTG Style */}
            <section className="py-10 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-[10px] font-black text-red-600  tracking-[0.4em] mb-4">{t_api(content.start_tag, content.start_tag_ar) || t('careers.landing.onboarding')}</h2>
                    <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-20 tracking-tight">{t_api(content.start_title, content.start_title_ar) || t('careers.landing.getting_started')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
                        <div className="relative group overflow-hidden rounded-3xl bg-slate-900 shadow-2xl h-[500px]">
                            <img 
                                src={getImg('intern_image', '/Hamdan-Residential%20Complex.jpg')} 
                                alt="Interns" 
                                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-[2s]" 
                            />
                            <div className="absolute inset-0 p-12 flex flex-col justify-end text-white bg-gradient-to-t from-red-900/90 via-transparent to-transparent">
                                <h4 className="text-2xl font-black mb-4 flex items-center justify-between  tracking-tighter">
                                    {t_api(content.intern_title, content.intern_title_ar) || t('careers.landing.internship')} 
                                    <ChevronRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" />
                                </h4>
                                <p className="text-lg font-bold leading-relaxed text-slate-200">
                                    {t_api(content.intern_desc, content.intern_desc_ar) || 'The HTG Internship programme aims to train and develop Omani youth to create the future pipeline for our graduate intake.'}
                                </p>
                            </div>
                        </div>
                        <div className="relative group overflow-hidden rounded-3xl bg-slate-900 shadow-2xl h-[500px]">
                            <img 
                                src={getImg('experienced_image', '/contact_banner.jpg')} 
                                alt="Professionals" 
                                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-[2s]" 
                            />
                            <div className="absolute inset-0 p-12 flex flex-col justify-end text-white bg-gradient-to-t from-black/90 via-transparent to-transparent">
                                <h4 className="text-2xl font-black mb-4 flex items-center justify-between  tracking-tighter">
                                    {t_api(content.experienced_title, content.experienced_title_ar) || t('careers.landing.professionals')} 
                                    <ChevronRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" />
                                </h4>
                                <p className="text-lg font-bold leading-relaxed text-slate-200">
                                    {t_api(content.experienced_desc, content.experienced_desc_ar) || 'At Hamdan Trading Group we look for exceptional people from different backgrounds and professions with the right skill set.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section - Premium Style */}
            <section className="py-10 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 text-center mb-16">
                    <h2 className="text-[10px] font-black text-red-600  tracking-[0.4em] mb-4">{t_api(content.test_tag, content.test_tag_ar) || t('careers.landing.feedback')}</h2>
                    <h3 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight leading-none ">{t_api(content.test_title, content.test_title_ar) || t('careers.landing.what_employees_say')}</h3>
                </div>

                <div className="max-w-6xl mx-auto px-6">
                    <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col lg:flex-row items-stretch">
                        <div className="lg:w-1/2 p-16 md:p-24 flex flex-col justify-center bg-white relative">
                            {/* Large quote mark */}
                            <div className="absolute top-10 left-10 text-red-600/5 font-serif text-[15rem] leading-none select-none">“</div>
                            
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className="relative z-10"
                            >
                                <p className="text-2xl italic text-slate-600 font-medium mb-12 leading-relaxed">
                                    "{t_api(content.test_quote, content.test_quote_ar) || `Working in Hamdan Trading Group and being exposed to group companies has developed my negotiation and traffic analysis skills with global partners and enabled us to introduce new business opportunities.`}"
                                </p>
                                
                                <div className="flex flex-col">
                                    <span className="text-2xl font-black text-slate-900  tracking-wider">{t_api(content.test_name, content.test_name_ar) || 'Ahmed Al Harthi'}</span>
                                    <span className="text-sm font-black text-red-600  tracking-widest mt-2">{t_api(content.test_pos, content.test_pos_ar) || 'Operations Department'}</span>
                                </div>
                            </motion.div>
                        </div>
                        
                        <div className="lg:w-1/2 relative min-h-[500px]">
                            <img 
                                src={getImg('test_image', '/News/Picture01.jpg')} 
                                alt="Employee" 
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default JobPortalLanding;
